import React from 'react';
import { Box, Button, Chip, IconButton, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { ICampaignCategory } from '../../../../components/EsForm/CampaignCategorySelector';
import {
    emptyPayoutsForTiers,
    SUGGESTED_ADDITIONAL_PRIZES,
    SUGGESTED_PLACEMENTS,
    tierLabelsFromCategory,
    type PrizePayout,
    type PrizePlacement,
} from '../awardPresets';
import type { ContestWizardState } from '../types';
import { isCategoryFilled } from '../../contestManage/contestCategoryWizardUtils';
import { ContestTiersAwardsHelpButton } from '../ContestTiersAwardsHelpButton';
import { editorialHeadlineSx, editorialInputSx, editorialLabelSx, midnight } from '../midnightStyles';

interface Props {
    state: ContestWizardState;
    onChange: (patch: Partial<ContestWizardState>) => void;
    onOpenAiAssist: () => void;
}

function parsePrize(raw: string): number {
    const n = parseFloat(raw.replace(/[^\d.]/g, ''));
    return Number.isNaN(n) ? 0 : n;
}

export function CategoryAwardsStep({ state, onChange, onOpenAiAssist }: Props) {
    const categories = state.categories.filter(isCategoryFilled);
    const rawIdx = state.editingCategoryIndex ?? 0;
    const rawCategory = state.categories[rawIdx];
    const idx =
        rawCategory && isCategoryFilled(rawCategory)
            ? categories.findIndex(
                  c =>
                      c.category_name === rawCategory.category_name &&
                      c.category_description === rawCategory.category_description,
              )
            : 0;
    const safeIdx = idx >= 0 ? idx : 0;
    const category = categories[safeIdx];

    if (!state.categories_enabled) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography sx={{ ...editorialHeadlineSx, fontSize: 32, color: midnight.onSurface }}>
                    Awards & Prizes
                </Typography>
                <Typography sx={{ color: midnight.onSurfaceVariant, fontSize: 14 }}>
                    Turn on submission categories in Entry Rules (step 5) to assign prizes by tier.
                </Typography>
            </Box>
        );
    }

    if (!category) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography sx={{ ...editorialHeadlineSx, fontSize: 32, color: midnight.onSurface }}>
                    Awards & Prizes
                </Typography>
                <Typography sx={{ color: midnight.onSurfaceVariant, fontSize: 14 }}>
                    Add at least one submission category in the previous step first.
                </Typography>
            </Box>
        );
    }

    const tierLabels = tierLabelsFromCategory(category);
    const placements: PrizePlacement[] = (category.prize_structure || []).map(p => ({
        position: p.position,
        payouts: (p.payouts || []).map(po => ({
            label: po.label,
            prize: po.prize,
            additional_prize: po.additional_prize || [],
            ...(po._id ? { _id: po._id } : {}),
        })),
    }));

    const setCategory = (next: ICampaignCategory) => {
        const full = [...state.categories];
        const fullIdx = full.findIndex(
            c =>
                isCategoryFilled(c) &&
                c.category_name === categories[safeIdx]?.category_name &&
                c.category_description === categories[safeIdx]?.category_description,
        );
        const targetIdx = fullIdx >= 0 ? fullIdx : rawIdx;
        full[targetIdx] = next;
        onChange({ categories: full, editingCategoryIndex: targetIdx });
    };

    const setPlacements = (next: PrizePlacement[]) => {
        setCategory({ ...category, prize_structure: next });
    };

    const addPlacement = (position: string) => {
        if (placements.some(p => p.position === position)) return;
        setPlacements([...placements, { position, payouts: emptyPayoutsForTiers(tierLabels) }]);
    };

    const updatePlacement = (placeIndex: number, patch: Partial<PrizePlacement>) => {
        const next = placements.map((p, i) => (i === placeIndex ? { ...p, ...patch } : p));
        setPlacements(next);
    };

    const updatePayout = (
        placeIndex: number,
        payoutIndex: number,
        patch: Partial<Pick<PrizePayout, 'prize' | 'label' | 'additional_prize'>>,
    ) => {
        const next = placements.map((p, i) => {
            if (i !== placeIndex) return p;
            const payouts = p.payouts.map((po, j) => (j === payoutIndex ? { ...po, ...patch } : po));
            return { ...p, payouts };
        });
        setPlacements(next);
    };

    const updateAdditionalPrize = (placeIndex: number, payoutIndex: number, addIndex: number, value: string) => {
        const payout = placements[placeIndex]?.payouts[payoutIndex];
        if (!payout) return;
        const extras = [...(payout.additional_prize || [])];
        extras[addIndex] = value;
        updatePayout(placeIndex, payoutIndex, { additional_prize: extras });
    };

    const addAdditionalPrize = (placeIndex: number, payoutIndex: number) => {
        const payout = placements[placeIndex]?.payouts[payoutIndex];
        if (!payout) return;
        updatePayout(placeIndex, payoutIndex, {
            additional_prize: [...(payout.additional_prize || []), ''],
        });
    };

    const removeAdditionalPrize = (placeIndex: number, payoutIndex: number, addIndex: number) => {
        const payout = placements[placeIndex]?.payouts[payoutIndex];
        if (!payout) return;
        updatePayout(placeIndex, payoutIndex, {
            additional_prize: (payout.additional_prize || []).filter((_, i) => i !== addIndex),
        });
    };

    const addSuggestedAdditionalPrize = (placeIndex: number, payoutIndex: number, label: string) => {
        const payout = placements[placeIndex]?.payouts[payoutIndex];
        if (!payout) return;
        const existing = (payout.additional_prize || []).map(p => p.trim().toLowerCase());
        if (existing.includes(label.trim().toLowerCase())) return;
        updatePayout(placeIndex, payoutIndex, {
            additional_prize: [...(payout.additional_prize || []).filter(p => p.trim()), label],
        });
    };

    const removePlacement = (placeIndex: number) => {
        setPlacements(placements.filter((_, i) => i !== placeIndex));
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ ...editorialHeadlineSx, fontSize: 32, color: midnight.onSurface }}>
                        Awards & Prizes
                    </Typography>
                    <ContestTiersAwardsHelpButton size="medium" />
                </Box>
                <Typography sx={{ color: midnight.onSurfaceVariant, mt: 1, fontSize: 14 }}>
                    For{' '}
                    <strong style={{ color: midnight.onSurface }}>{category.category_name || 'this category'}</strong>,
                    set cash and non-monetary prizes per placement and submission tier ({tierLabels.join(', ')}).
                </Typography>
            </Box>

            {categories.length > 1 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {categories.map((c, i) => (
                        <Button
                            key={i}
                            size="small"
                            onClick={() => {
                                const fullIdx = state.categories.findIndex(
                                    fc =>
                                        isCategoryFilled(fc) &&
                                        fc.category_name === c.category_name &&
                                        fc.category_description === c.category_description,
                                );
                                onChange({
                                    editingCategoryIndex: fullIdx >= 0 ? fullIdx : i,
                                });
                            }}
                            sx={{
                                minWidth: 32,
                                borderRadius: 999,
                                bgcolor: i === safeIdx ? midnight.primary : midnight.surfaceHighest,
                                color: i === safeIdx ? '#392700' : midnight.onSurfaceVariant,
                                fontWeight: 700,
                            }}>
                            {i + 1}
                        </Button>
                    ))}
                </Box>
            )}

            <Box
                sx={{
                    p: 3,
                    borderRadius: '12px',
                    border: `1px solid ${midnight.outline}`,
                }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <AutoAwesomeIcon sx={{ color: midnight.secondary }} />
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 700, mb: 0.5, color: midnight.onSurface }}>
                            AI Assistant
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: midnight.onSurfaceVariant, mb: 2 }}>
                            We use this category&apos;s tiers to suggest placements, cash amounts, and perks like
                            certificates. Pick a layout and budget level, then edit anything below.
                        </Typography>
                        <Button
                            onClick={onOpenAiAssist}
                            sx={{
                                color: midnight.secondary,
                                fontWeight: 700,
                                fontSize: 11,
                                letterSpacing: '0.15em',
                                textTransform: 'uppercase',
                                p: 0,
                                minWidth: 0,
                                '&:hover': {
                                    bgcolor: 'transparent',
                                    color: midnight.secondary,
                                    opacity: 0.85,
                                },
                            }}>
                            Generate awards →
                        </Button>
                    </Box>
                </Box>
            </Box>

            <Box>
                <Typography sx={{ ...editorialLabelSx, mb: 1 }}>Quick add placement</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {SUGGESTED_PLACEMENTS.map(label => (
                        <Chip
                            key={label}
                            label={label}
                            onClick={() => addPlacement(label)}
                            disabled={placements.some(p => p.position === label)}
                            sx={{
                                fontWeight: 600,
                                bgcolor: midnight.surfaceHighest,
                                color: midnight.onSurface,
                                '&:hover': { bgcolor: midnight.surfaceContainer },
                            }}
                        />
                    ))}
                </Box>
            </Box>

            {placements.length === 0 && (
                <Typography sx={{ fontSize: 13, color: midnight.onSurfaceVariant }}>
                    No placements yet — use a suggestion above, add manually, or run AI assist.
                </Typography>
            )}

            {placements.map((placement, placeIndex) => (
                <Box
                    key={`placement-${placeIndex}`}
                    sx={{
                        bgcolor: midnight.surfaceLow,
                        p: 3,
                        borderRadius: '12px',
                        border: '1px solid rgba(255,255,255,0.06)',
                    }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography sx={editorialLabelSx}>Placement {placeIndex + 1}</Typography>
                        <IconButton size="small" onClick={() => removePlacement(placeIndex)}>
                            <DeleteOutlineIcon sx={{ color: midnight.onSurfaceVariant, fontSize: 18 }} />
                        </IconButton>
                    </Box>
                    <TextField
                        fullWidth
                        label="Placement name"
                        value={placement.position}
                        onChange={e => updatePlacement(placeIndex, { position: e.target.value })}
                        sx={{ ...editorialInputSx, mb: 2 }}
                    />
                    <Typography sx={{ ...editorialLabelSx, mb: 1.5 }}>Prizes by submission tier</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        {placement.payouts.map((payout, payoutIndex) => {
                            const extras = payout.additional_prize || [];
                            const hasExtras = extras.length > 0;
                            return (
                                <Box
                                    key={`${payout.label}-${payoutIndex}`}
                                    sx={{
                                        p: 2,
                                        borderRadius: '10px',
                                        border: '1px solid rgba(255,255,255,0.06)',
                                        bgcolor: midnight.surfaceContainer,
                                    }}>
                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                            color: midnight.primary,
                                            fontSize: 14,
                                            mb: 1.5,
                                        }}>
                                        {payout.label}
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        type="text"
                                        inputMode="decimal"
                                        label="Cash prize (USD)"
                                        placeholder="e.g. 500"
                                        value={payout.prize ? String(payout.prize) : ''}
                                        onChange={e =>
                                            updatePayout(placeIndex, payoutIndex, {
                                                prize: parsePrize(e.target.value),
                                            })
                                        }
                                        sx={{ ...editorialInputSx, mb: 2 }}
                                    />
                                    <Typography sx={{ ...editorialLabelSx, mb: 1 }}>
                                        Additional prizes (optional)
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            color: midnight.onSurfaceVariant,
                                            mb: 1,
                                        }}>
                                        Non-monetary perks — certificates, trophies, gear, exposure, etc.
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: hasExtras ? 1.5 : 0 }}>
                                        {SUGGESTED_ADDITIONAL_PRIZES.map(suggestion => {
                                            const taken = extras.some(
                                                p => p.trim().toLowerCase() === suggestion.toLowerCase(),
                                            );
                                            return (
                                                <Chip
                                                    key={suggestion}
                                                    label={suggestion}
                                                    size="small"
                                                    disabled={taken}
                                                    onClick={() =>
                                                        addSuggestedAdditionalPrize(placeIndex, payoutIndex, suggestion)
                                                    }
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: 11,
                                                        bgcolor: midnight.surfaceHighest,
                                                        color: midnight.onSurface,
                                                        '&:hover': { bgcolor: midnight.surfaceLow },
                                                    }}
                                                />
                                            );
                                        })}
                                    </Box>
                                    {extras.map((extra, addIndex) => (
                                        <Box
                                            key={addIndex}
                                            sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="e.g. Certificate, Trophy, gear bundle"
                                                value={extra}
                                                onChange={e =>
                                                    updateAdditionalPrize(
                                                        placeIndex,
                                                        payoutIndex,
                                                        addIndex,
                                                        e.target.value,
                                                    )
                                                }
                                                sx={editorialInputSx}
                                            />
                                            <IconButton
                                                size="small"
                                                onClick={() => removeAdditionalPrize(placeIndex, payoutIndex, addIndex)}
                                                sx={{ mt: 0.5 }}>
                                                <DeleteOutlineIcon
                                                    sx={{ color: midnight.onSurfaceVariant, fontSize: 18 }}
                                                />
                                            </IconButton>
                                        </Box>
                                    ))}
                                    <Button
                                        size="small"
                                        startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                                        onClick={() => addAdditionalPrize(placeIndex, payoutIndex)}
                                        sx={{
                                            mt: 0.5,
                                            color: midnight.secondary,
                                            fontWeight: 600,
                                            fontSize: 12,
                                            textTransform: 'none',
                                        }}>
                                        Add additional prize
                                    </Button>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            ))}

            <Button
                startIcon={<AddIcon />}
                onClick={() => addPlacement(`Placement ${placements.length + 1}`)}
                sx={{
                    alignSelf: 'flex-start',
                    color: midnight.primary,
                    fontWeight: 700,
                }}>
                Add placement
            </Button>
        </Box>
    );
}
